import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Stack,
    TextField,
} from '@mui/material';
import { BulkSwapRequest } from '../../interfaces/shift/RosterCell';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';

const KNOWN_SWAP_ERRORS: Record<string, string> = {
    INVALID_TIME_RANGE: 'The date range is invalid (from must be on or before to).',
    SAME_USER_SWAP: 'User A and User B must be different people.',
    SWAP_BLOCKED_ATTENDANCE_PRESENT: 'Cannot swap — attendance has already been recorded for one or more days in this range.',
};

const parseSwapError = (err: unknown): string => {
    const axErr = err as any;
    const msg: string = axErr?.response?.data?.message || axErr?.response?.data || axErr?.message || '';
    for (const [key, label] of Object.entries(KNOWN_SWAP_ERRORS)) {
        if (msg.includes(key)) return label;
    }
    return msg || 'Bulk swap failed.';
};

const todayStr = () => toLocalIsoDate();

interface SimpleUser {
    id: string;
    username: string;
    name?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    institutionId: string;
    users: SimpleUser[];
    onSaved: () => void;
}

const BulkSwapDialog: React.FC<Props> = ({ open, onClose, institutionId, users, onSaved }) => {
    const toast = useToast();

    const [userA, setUserA] = useState<SimpleUser | null>(null);
    const [userB, setUserB] = useState<SimpleUser | null>(null);
    const [fromDate, setFromDate] = useState(todayStr());
    const [toDate, setToDate]     = useState(todayStr());
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{
        userA?: string; userB?: string; fromDate?: string; toDate?: string;
    }>({});

    useEffect(() => {
        if (open) {
            setUserA(null);
            setUserB(null);
            setFromDate(todayStr());
            setToDate(todayStr());
            setSaving(false);
            setErrors({});
        }
    }, [open]);

    const displayUser = (u: SimpleUser) => u.name || u.username;

    const validate = (): boolean => {
        const errs: typeof errors = {};
        if (!userA) errs.userA = 'Select User A';
        if (!userB) errs.userB = 'Select User B';
        if (userA && userB && userA.id === userB.id) {
            errs.userB = 'User B must differ from User A';
        }
        if (!fromDate) errs.fromDate = 'From date is required';
        if (!toDate)   errs.toDate   = 'To date is required';
        if (fromDate && toDate && fromDate > toDate) {
            errs.toDate = 'To date must be on or after from date';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body: BulkSwapRequest = {
                userIdA: userA!.id,
                userIdB: userB!.id,
                fromDate,
                toDate,
            };
            const result = await ShiftRosterService.bulkSwap(institutionId, body);
            toast.showSuccess(`Swapped ${result.length} instance(s)`);
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, parseSwapError(err));
        } finally {
            setSaving(false);
        }
    };

    // Options for B: exclude user A
    const optionsB = userA ? users.filter(u => u.id !== userA.id) : users;

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'shifts.swap.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk Swap Shifts</DialogTitle>
            <DialogContent>
                <Stack data-help-section="shifts.swap.section.details" spacing={2} sx={{ pt: 1 }}>
                    {/* User A */}
                    <Autocomplete
                        options={users}
                        value={userA}
                        onChange={(_e, val) => { setUserA(val); setErrors(prev => ({ ...prev, userA: undefined })); }}
                        getOptionLabel={displayUser}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                data-help="shifts.swap.field.userA" label="User A"
                                required
                                error={!!errors.userA}
                                helperText={errors.userA}
                                inputProps={{ ...params.inputProps, 'data-testid': 'bulk-swap-userA' }}
                            />
                        )}
                        data-testid="bulk-swap-userA-auto"
                    />

                    {/* User B */}
                    <Autocomplete
                        options={optionsB}
                        value={userB}
                        onChange={(_e, val) => { setUserB(val); setErrors(prev => ({ ...prev, userB: undefined })); }}
                        getOptionLabel={displayUser}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                data-help="shifts.swap.field.userB" label="User B"
                                required
                                error={!!errors.userB}
                                helperText={errors.userB}
                                inputProps={{ ...params.inputProps, 'data-testid': 'bulk-swap-userB' }}
                            />
                        )}
                        data-testid="bulk-swap-userB-auto"
                    />

                    {/* Date range */}
                    <TextField
                        data-help="shifts.swap.field.from" label="From"
                        type="date"
                        fullWidth
                        value={fromDate}
                        onChange={e => { setFromDate(e.target.value); setErrors(prev => ({ ...prev, fromDate: undefined })); }}
                        error={!!errors.fromDate}
                        helperText={errors.fromDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'bulk-swap-from' }}
                    />
                    <TextField
                        data-help="shifts.swap.field.to" label="To"
                        type="date"
                        fullWidth
                        value={toDate}
                        onChange={e => { setToDate(e.target.value); setErrors(prev => ({ ...prev, toDate: undefined })); }}
                        error={!!errors.toDate}
                        helperText={errors.toDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'bulk-swap-to' }}
                    />

                    {/* General error placeholder (shown via toast) */}
                    {errors.userA && !userA && (
                        <FormHelperText error>{errors.userA}</FormHelperText>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="shifts.swap.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={saving}
                    data-help="shifts.swap.save" data-testid="bulk-swap-save-btn"
                >
                    {saving ? 'Swapping…' : 'Swap'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkSwapDialog;
